/** MIME type of a music file by its extension – unplayable formats are skipped before loading. */
const MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  m4a: 'audio/mp4',
  mp4: 'audio/mp4',
  mp3: 'audio/mpeg',
  ogg: 'audio/ogg',
};

/** Smallest gain an exponential fade can start from (0 is not allowed). */
const SILENT_GAIN = 0.0001;

/**
 * Background music, streamed by an <audio> element instead of decoding the whole track into memory: the
 * 2-minute loop decoded to ~45 MB of samples, which phones silently refused (no music on mobile). The element
 * is routed through Web Audio so the volume and the fade also work on iOS (media element volume is fixed there).
 * Browsers only start audio from a user gesture – play() is called from the gesture handlers.
 */
export class MusicTrack {
  private readonly element: HTMLAudioElement;
  private readonly sources: readonly string[];
  private sourceIndex = 0;
  private gain: GainNode | null = null;

  constructor(
    private readonly context: AudioContext,
    candidates: readonly string[],
    private readonly volume: number,
    private readonly fadeInSeconds: number,
    private enabled: boolean,
  ) {
    this.element = new Audio();
    this.element.loop = true;
    // only the header up front: the 2.2 MB track streams once it plays and no longer competes with the models
    // behind the loading screen (was 'auto')
    this.element.preload = 'metadata';
    this.sources = candidates.filter((url) => this.canPlay(url));
    this.element.addEventListener('error', this.onError);
    this.loadSource();
    // a hidden tab keeps no music running (the game pauses itself); the next tap starts it again
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.element.pause();
    });
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  /** From a user gesture: starts the music if it is switched on and not playing yet. */
  play(): void {
    if (!this.enabled || !this.element.paused || this.sourceIndex >= this.sources.length) return;
    this.connect();
    this.fadeIn();
    // Refused (no gesture yet, source still loading): the next gesture tries again.
    this.element.play().catch(() => undefined);
  }

  /** Music on / off; switching on must come from a user gesture (the toggle button) to be allowed. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled) this.play();
    else this.element.pause();
  }

  private canPlay(url: string): boolean {
    const extension = url.split('.').pop()?.toLowerCase() ?? '';
    const mime = MIME_BY_EXTENSION[extension];
    return mime !== undefined && this.element.canPlayType(mime) !== '';
  }

  /** Once: element → gain → speakers (a media element can only be connected to one source node). */
  private connect(): void {
    if (this.gain) return;
    const source = this.context.createMediaElementSource(this.element);
    this.gain = this.context.createGain();
    this.gain.gain.value = SILENT_GAIN;
    source.connect(this.gain).connect(this.context.destination);
  }

  private fadeIn(): void {
    if (!this.gain) return;
    const now = this.context.currentTime;
    this.gain.gain.cancelScheduledValues(now);
    this.gain.gain.setValueAtTime(SILENT_GAIN, now);
    this.gain.gain.exponentialRampToValueAtTime(this.volume, now + this.fadeInSeconds);
  }

  private loadSource(): void {
    const url = this.sources[this.sourceIndex];
    if (url !== undefined) this.element.src = url;
  }

  /** A file that does not load (missing, not decodable): try the next candidate. */
  private readonly onError = (): void => {
    this.sourceIndex += 1;
    this.loadSource();
  };
}
